// ============================================================
// FILE: web-app/src/App.js
// TỐI ƯU: Lazy Loading + Suspense + Socket.io User Presence
// ============================================================

import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { AuthContext } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import api from './services/api';
import { AvatarPreviewHost } from './components/AvatarXem';
import './App.css';
import './styles/responsive.css';
const AIAssistant = lazy(() => import('./components/AIAssistant'));
const ChatWidget = lazy(() => import('./components/Chat/ChatWidget'));
const ChatStorage = lazy(() => import('./pages/ChatStorage/ChatStorage'));
const NotifySetup = lazy(() => import('./pages/NotifySetup/NotifySetup'));
// ============================================================
// LOADING COMPONENT
// ============================================================
const PageLoading = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh'
  }}>
    <div style={{ textAlign: 'center' }}>
      <Spin size="large" />
      <p style={{ marginTop: 16, color: '#666' }}>Đang tải...</p>
    </div>
  </div>
);

const ContentLoading = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '60vh'
  }}>
    <Spin size="large" />
  </div>
);

// ============================================================
// LAZY LOAD COMPONENTS
// ============================================================
const GoodsReport = lazy(() => import('./pages/Reports/GoodsReport'));
const MainLayout = lazy(() => import('./components/Layout/MainLayout'));
const CuaAn = lazy(() => import('./pages/Auth/CuaAn'));   // cửa ẩn: bức tranh trước form đăng nhập
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const CompanyInfo = lazy(() => import('./pages/CompanyInfo/CompanyInfo'));
const ProductList = lazy(() => import('./pages/Products/ProductList'));
const CategoryList = lazy(() => import('./pages/Categories/CategoryList'));
const PriceListReport = lazy(() => import('./pages/PriceList/PriceListReport'));
const BangGiaSi = lazy(() => import('./pages/PriceList/BangGiaSi'));   // bảng giá khách sỉ
const PricePeriodList = lazy(() => import('./pages/PriceUpdate/PricePeriodList'));
const PricePeriodDetail = lazy(() => import('./pages/PriceUpdate/PricePeriodDetail'));
const PriceFormulaConfig = lazy(() => import('./pages/PriceUpdate/PriceFormulaConfig'));
const CustomerList = lazy(() => import('./pages/Customers/CustomerList'));
const SupplierList = lazy(() => import('./pages/Suppliers/SupplierList'));
const OrderList = lazy(() => import('./pages/Orders/OrderList'));
const CreateOrder = lazy(() => import('./pages/Orders/CreateOrder'));
const OrderPrint = lazy(() => import('./pages/Orders/OrderPrint'));
const PurchaseOrderList = lazy(() => import('./pages/PurchaseOrders/PurchaseOrderList'));
const CreatePurchaseOrder = lazy(() => import('./pages/PurchaseOrders/CreatePurchaseOrder'));
const TransactionList = lazy(() => import('./pages/Transactions/TransactionList'));
const TransactionPrint = lazy(() => import('./pages/Transactions/TransactionPrint'));
const PaymentRequestPrint = lazy(() => import('./pages/Transactions/PaymentRequestPrint'));
const OrderReport = lazy(() => import('./pages/Reports/OrderReport'));
const InventoryReport = lazy(() => import('./pages/Reports/InventoryReport'));
const SummaryReport = lazy(() => import('./pages/Reports/SummaryReport'));
const CustomerDebtReport = lazy(() => import('./pages/Reports/CustomerDebtReport'));
const ProfitReport = lazy(() => import('./pages/Reports/ProfitReport'));
const PurchaseSettlement = lazy(() => import('./pages/Reports/PurchaseSettlement'));
const CustomerDiscount = lazy(() => import('./pages/Reports/CustomerDiscount'));
const DebtReminder = lazy(() => import('./pages/DebtReminder/DebtReminder'));
const VATInvoice = lazy(() => import('./pages/Invoice/VATInvoice'));
const HoaDonDienTu = lazy(() => import('./pages/Invoice/HoaDonDienTu'));   // hóa đơn điện tử EasyInvoice: nháp / đã ký
const HoaDonDauVao = lazy(() => import('./pages/Invoice/HoaDonDauVao'));   // hóa đơn đầu vào: quăng tệp PDF, AI đọc, món có / chưa có đầu vào
const BangKeGtgt = lazy(() => import('./pages/Invoice/BangKeGtgt'));       // bảng kê thuế GTGT theo kỳ: bán ra + mua vào + chỉ tiêu tờ khai 01/GTGT
const BangKe02 = lazy(() => import('./pages/Invoice/BangKe02'));           // bảng kê 02/TNDN: hàng mua không có hóa đơn theo tháng (tự tách từ đơn mua)
const DoiChieuCongThue = lazy(() => import('./pages/Invoice/DoiChieuCongThue'));   // đối chiếu hóa đơn mua vào / bán ra với tệp Excel của cổng thuế
const QuickQuote = lazy(() => import('./pages/QuickQuote/QuickQuote'));
const CustomerPortal = lazy(() => import('./pages/Portal/CustomerPortal'));
const DebtReconciliation = lazy(() => import('./pages/DebtReconciliation/DebtReconciliation'));
const PortalAccessLog = lazy(() => import('./pages/PortalAccessLog/PortalAccessLog'));
const ContractList = lazy(() => import('./pages/Contracts/ContractList'));
const UserManagement = lazy(() => import('./pages/Users/UserManagement'));
const BaoMat = lazy(() => import('./pages/BaoMat/BaoMat'));
const BackupManagement = lazy(() => import('./pages/Backup/BackupManagement'));
const QuickPurchase = lazy(() => import('./pages/QuickPurchase/QuickPurchase'));
const SmartShopping = lazy(() => import('./pages/SmartShopping/SmartShopping'));
const StockTaking = lazy(() => import('./pages/StockTaking/StockTaking'));
const CompanyDocs = lazy(() => import('./pages/CompanyDocs/CompanyDocs'));
const DocTemplates = lazy(() => import('./pages/DocTemplates/DocTemplates'));
const OrderPublicView = lazy(() => import('./pages/Public/OrderPublicView'));
const QuotePublicView = lazy(() => import('./pages/Public/QuotePublicView'));
const KyVanBan = lazy(() => import('./pages/Public/KyVanBan'));
const XacMinhVanBan = lazy(() => import('./pages/Public/XacMinhVanBan'));
const KyHopDong = lazy(() => import('./pages/Public/KyHopDong'));
const OtpSettings = lazy(() => import('./pages/Settings/OtpSettings'));
const TelegramSettings = lazy(() => import('./pages/Settings/TelegramSettings'));
const CuaAnSettings = lazy(() => import('./pages/Settings/CuaAnSettings'));
const CaiDatHoaDonDienTu = lazy(() => import('./pages/Settings/CaiDatHoaDonDienTu'));
const CaiDatHoaDonDauVao = lazy(() => import('./pages/Settings/CaiDatHoaDonDauVao'));
const CaiDatChietKhauKhach = lazy(() => import('./pages/Settings/CaiDatChietKhauKhach'));
const CostSummary = lazy(() => import('./pages/Reports/CostSummary'));

// ============================================================
// MAIN APP COMPONENT
// ============================================================
function App() {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const storedPermissions = localStorage.getItem('permissions');
    
    if (token && userData) {
      setUser(JSON.parse(userData));
      setPermissions(storedPermissions ? JSON.parse(storedPermissions) : []);

      // Hỏi lại máy chủ cho chắc: nếu đổi tên hiển thị hay ảnh đại diện ở máy
      // khác thì máy này vẫn đang giữ bản cũ trong localStorage.
      api.get('/auth/me')
        .then(r => {
          const u = r.data?.data?.user;
          if (u) setUser(cu => ({ ...(cu || {}), ...u }));
        })
        .catch(() => { /* mất mạng thì cứ dùng bản đang có */ });
    }
    setLoading(false);
  }, []);

  // Ghi thông tin người dùng xuống máy mỗi khi nó đổi.
  //
  // Để ở HIỆU ỨNG chứ tuyệt đối không nhét vào trong hàm cập nhật state:
  // hàm cập nhật state phải thuần, mà `localStorage.setItem` thì NÉM LỖI được
  // (iPhone hết dung lượng, chặn ghi, chế độ riêng tư). Lỗi đó văng ngược ra
  // tận chỗ gọi -> hồ sơ đã lưu xong trên máy chủ mà màn hình vẫn báo lỗi.
  useEffect(() => {
    if (!user) return;
    try {
      localStorage.setItem('user', JSON.stringify(user));
    } catch (e) {
      // Không ghi được thì thôi: lần mở app sau /auth/me sẽ tải lại từ máy chủ
      console.warn('Không ghi được hồ sơ xuống máy:', e?.message);
    }
  }, [user]);

  // Cập nhật thông tin người dùng đang đăng nhập (đổi tên hiển thị, ảnh đại diện)
  const updateUser = (patch) => {
    setUser(cu => ({ ...(cu || {}), ...patch }));
  };

  const login = (userData, token, userPermissions = []) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('permissions', JSON.stringify(userPermissions));
    setUser(userData);
    setPermissions(userPermissions);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    setUser(null);
    setPermissions([]);
  };

  const hasPermission = (module, action = 'view') => {
    if (user?.role === 'admin') return true;
    return permissions.some(p => p.module === module && p.action === action);
  };

  const canView = (module) => hasPermission(module, 'view');
  const canCreate = (module) => hasPermission(module, 'create');
  const canEdit = (module) => hasPermission(module, 'edit');
  const canDelete = (module) => hasPermission(module, 'delete');

  if (loading) {
    return <PageLoading />;
  }

  return (
    <AuthContext.Provider value={{ 
      user,
      permissions,
      login,
      logout,
      updateUser,
      hasPermission,
      canView,
      canCreate,
      canEdit,
      canDelete
    }}>
      <SocketProvider user={user}>
        <Suspense fallback={<PageLoading />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={
              user ? <Navigate to="/" /> : <CuaAn />
            } />
{/* PUBLIC ROUTES - Không cần đăng nhập */}
<Route path="/order-view/:code/:token" element={
  <Suspense fallback={<PageLoading />}><OrderPublicView /></Suspense>
} />
<Route path="/bao-gia/:token" element={
  <Suspense fallback={<PageLoading />}><QuotePublicView /></Suspense>
} />
<Route path="/ky/:token" element={
  <Suspense fallback={<PageLoading />}><KyVanBan /></Suspense>
} />
<Route path="/xac-minh/:code" element={
  <Suspense fallback={<PageLoading />}><XacMinhVanBan /></Suspense>
} />
<Route path="/ky-hd/:token" element={
  <Suspense fallback={<PageLoading />}><KyHopDong /></Suspense>
} />
{/* CỔNG KHÁCH HÀNG - Tra cứu công nợ (công khai, token riêng) */}
<Route path="/portal" element={
  <Suspense fallback={<PageLoading />}><CustomerPortal /></Suspense>
} />
            {/* TRANG IN - KHÔNG CÓ LAYOUT */}
            <Route path="/transactions/payment-request" element={
              user ? <Suspense fallback={<ContentLoading />}><PaymentRequestPrint /></Suspense> : <Navigate to="/login" />
            } />
            <Route path="/transactions/print/:id" element={
              user ? <Suspense fallback={<ContentLoading />}><TransactionPrint /></Suspense> : <Navigate to="/login" />
            } />
            <Route path="/orders/print/:id" element={
              user ? <Suspense fallback={<ContentLoading />}><OrderPrint /></Suspense> : <Navigate to="/login" />
            } />

            {/* Protected routes - CÓ LAYOUT */}
            <Route path="/" element={user ? <MainLayout /> : <Navigate to="/login" />}>
              <Route index element={<Suspense fallback={<ContentLoading />}><Dashboard /></Suspense>} />
              <Route path="company" element={<Suspense fallback={<ContentLoading />}><CompanyInfo /></Suspense>} />
              <Route path="company-docs" element={<Suspense fallback={<ContentLoading />}><CompanyDocs /></Suspense>} />
              <Route path="doc-templates" element={<Suspense fallback={<ContentLoading />}><DocTemplates /></Suspense>} />
              <Route path="chat-storage" element={<Suspense fallback={<ContentLoading />}><ChatStorage /></Suspense>} />
              <Route path="notify-setup" element={<Suspense fallback={<ContentLoading />}><NotifySetup /></Suspense>} />
              <Route path="products" element={<Suspense fallback={<ContentLoading />}><ProductList /></Suspense>} />
              <Route path="categories" element={<Suspense fallback={<ContentLoading />}><CategoryList /></Suspense>} />
              <Route path="price-list-report" element={<Suspense fallback={<ContentLoading />}><PriceListReport /></Suspense>} />
              <Route path="bang-gia-si" element={<Suspense fallback={<ContentLoading />}><BangGiaSi /></Suspense>} />
              <Route path="price-update" element={<Suspense fallback={<ContentLoading />}><PricePeriodList /></Suspense>} />
              <Route path="price-update/:id" element={<Suspense fallback={<ContentLoading />}><PricePeriodDetail /></Suspense>} />
              <Route path="price-update/config" element={<Suspense fallback={<ContentLoading />}><PriceFormulaConfig /></Suspense>} />
              <Route path="customers" element={<Suspense fallback={<ContentLoading />}><CustomerList /></Suspense>} />
              <Route path="suppliers" element={<Suspense fallback={<ContentLoading />}><SupplierList /></Suspense>} />
              <Route path="orders" element={<Suspense fallback={<ContentLoading />}><OrderList /></Suspense>} />
              <Route path="orders/create" element={<Suspense fallback={<ContentLoading />}><CreateOrder /></Suspense>} />
              <Route path="orders/edit/:id" element={<Suspense fallback={<ContentLoading />}><CreateOrder /></Suspense>} />
              <Route path="purchase-orders" element={<Suspense fallback={<ContentLoading />}><PurchaseOrderList /></Suspense>} />
              <Route path="purchase-orders/create" element={<Suspense fallback={<ContentLoading />}><CreatePurchaseOrder /></Suspense>} />
              <Route path="purchase-orders/edit/:id" element={<Suspense fallback={<ContentLoading />}><CreatePurchaseOrder /></Suspense>} />
              <Route path="quick-purchase" element={<Suspense fallback={<ContentLoading />}><QuickPurchase /></Suspense>} />
              <Route path="smart-shopping" element={<Suspense fallback={<ContentLoading />}><SmartShopping /></Suspense>} />
              <Route path="stock-taking" element={<Suspense fallback={<ContentLoading />}><StockTaking /></Suspense>} />
              <Route path="transactions" element={<Suspense fallback={<ContentLoading />}><TransactionList /></Suspense>} />
              <Route path="reports/orders" element={<Suspense fallback={<ContentLoading />}><OrderReport /></Suspense>} />
              <Route path="reports/inventory" element={<Suspense fallback={<ContentLoading />}><InventoryReport /></Suspense>} />
              <Route path="reports/summary" element={<Suspense fallback={<ContentLoading />}><SummaryReport /></Suspense>} />
              <Route path="reports/customer-debt" element={<Suspense fallback={<ContentLoading />}><CustomerDebtReport /></Suspense>} />
              <Route path="debt-reminder" element={<Suspense fallback={<ContentLoading />}><DebtReminder /></Suspense>} />
              <Route path="reports/profit" element={<Suspense fallback={<ContentLoading />}><ProfitReport /></Suspense>} />
              <Route path="reports/purchase-settlement" element={<Suspense fallback={<ContentLoading />}><PurchaseSettlement /></Suspense>} />
              <Route path="reports/customer-discount" element={<Suspense fallback={<ContentLoading />}><CustomerDiscount /></Suspense>} />
              <Route path="invoice/vat" element={<Suspense fallback={<ContentLoading />}><VATInvoice /></Suspense>} />
              <Route path="invoice/dien-tu" element={<Suspense fallback={<ContentLoading />}><HoaDonDienTu /></Suspense>} />
              <Route path="invoice/dau-vao" element={<Suspense fallback={<ContentLoading />}><HoaDonDauVao /></Suspense>} />
              <Route path="invoice/bang-ke-thue" element={<Suspense fallback={<ContentLoading />}><BangKeGtgt /></Suspense>} />
              <Route path="invoice/bang-ke-02" element={<Suspense fallback={<ContentLoading />}><BangKe02 /></Suspense>} />
              <Route path="invoice/doi-chieu-cong-thue" element={<Suspense fallback={<ContentLoading />}><DoiChieuCongThue /></Suspense>} />
              <Route path="quick-quote" element={<Suspense fallback={<ContentLoading />}><QuickQuote /></Suspense>} />
              <Route path="debt-reconciliation" element={<Suspense fallback={<ContentLoading />}><DebtReconciliation /></Suspense>} />
              <Route path="portal-access-log" element={<Suspense fallback={<ContentLoading />}><PortalAccessLog /></Suspense>} />
              <Route path="contracts" element={<Suspense fallback={<ContentLoading />}><ContractList /></Suspense>} />
              <Route path="users" element={<Suspense fallback={<ContentLoading />}><UserManagement /></Suspense>} />
              <Route path="bao-mat" element={<Suspense fallback={<ContentLoading />}><BaoMat /></Suspense>} />
              <Route path="cai-dat-otp" element={<Suspense fallback={<ContentLoading />}><OtpSettings /></Suspense>} />
              <Route path="cai-dat-telegram" element={<Suspense fallback={<ContentLoading />}><TelegramSettings /></Suspense>} />
              <Route path="cai-dat-cua" element={<Suspense fallback={<ContentLoading />}><CuaAnSettings /></Suspense>} />
              <Route path="cai-dat-hoa-don" element={<Suspense fallback={<ContentLoading />}><CaiDatHoaDonDienTu /></Suspense>} />
              <Route path="cai-dat-hoa-don-dau-vao" element={<Suspense fallback={<ContentLoading />}><CaiDatHoaDonDauVao /></Suspense>} />
              <Route path="cai-dat-chiet-khau" element={<Suspense fallback={<ContentLoading />}><CaiDatChietKhauKhach /></Suspense>} />
              <Route path="backup" element={<Suspense fallback={<ContentLoading />}><BackupManagement /></Suspense>} />
              <Route path="/reports/cost-summary" element={<CostSummary />} />
              <Route path="/transactions/payment-request" element={<PaymentRequestPrint />} />
              <Route path="reports/goods" element={<Suspense fallback={<ContentLoading />}><GoodsReport /></Suspense>} />
            </Route>

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Suspense>
        {user && <Suspense fallback={null}><AIAssistant /></Suspense>}
        {user && <Suspense fallback={null}><ChatWidget /></Suspense>}
        {/* Khung xem ảnh đại diện — CHỈ MỘT cho cả phần mềm, nếu để mỗi
            ảnh tự dựng một khung thì bấm liên tiếp sẽ chồng khung, hiện nhầm ảnh */}
        <AvatarPreviewHost />
      </SocketProvider>
    </AuthContext.Provider>
  );
}

export default App;